import { useCallback, useEffect } from 'react';
import { STORAGE_FILES, STORAGE_WORKING, STORAGE_PREFIX } from '../constants';
import { useUI, useEditorState } from '../contexts';

function clearAtlasStorage() {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      keys.push(key);
    }
  }
  keys.forEach((k) => localStorage.removeItem(k));
}

type ProjectCaches = {
  files: Record<string, string>;
  working: Record<string, string>;
};

type UseCacheManagerParams = {
  activeProject: string;
  projectCaches: ProjectCaches;
  setPersistedFiles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setWorkingFiles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setFetchedFiles: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setSelectedNodeId: (id: string | null) => void;
  setCacheVersion: React.Dispatch<React.SetStateAction<number>>;
};

export function useCacheManager({
  activeProject,
  projectCaches,
  setPersistedFiles,
  setWorkingFiles,
  setFetchedFiles,
  setSelectedNodeId,
  setCacheVersion,
}: UseCacheManagerParams) {
  // Get UI state from context
  const { setStatus } = useUI();

  // Get editor state from context
  const { setCode, setActiveFile, setOpenTabs } = useEditorState();
  // Sync project caches when they change
  useEffect(() => {
    setPersistedFiles(projectCaches.files);
    setWorkingFiles(projectCaches.working);
    setFetchedFiles({});
    setOpenTabs([]);
    setActiveFile(null);
  }, [projectCaches, setPersistedFiles, setWorkingFiles, setFetchedFiles, setOpenTabs, setActiveFile]);

  const handleClearCache = useCallback(() => {
    setPersistedFiles({});
    setWorkingFiles({});
    setFetchedFiles({});
    setOpenTabs([]);
    setActiveFile(null);
    setSelectedNodeId(null);
    setCode('');
    setStatus('Önbellek temizlendi');
    localStorage.removeItem(`${STORAGE_FILES}:${activeProject}`);
    localStorage.removeItem(`${STORAGE_WORKING}:${activeProject}`);
    clearAtlasStorage();
    setCacheVersion((v) => v + 1);
  }, [
    activeProject,
    setPersistedFiles,
    setWorkingFiles,
    setFetchedFiles,
    setOpenTabs,
    setActiveFile,
    setSelectedNodeId,
    setCode,
    setStatus,
    setCacheVersion,
  ]);

  return { handleClearCache };
}
